import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, InputAdornment, Skeleton, Button, useTheme,
  useMediaQuery, alpha, Chip, Stack, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Drawer, Pagination, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import {
  useGetMyMessagesQuery,
  useGetMyMessageThreadQuery,
  useGetMyMessageRecipientsQuery,
  useSendMyMessageMutation,
  useMarkMyMessageReadMutation,
  useGetMyStudentsQuery,
  useGetMyActiveMessageTemplatesQuery,
  useGetMyAdminMessageRecipientsQuery,
  useSendMyAdminMessageMutation,
} from "../../Api/attoDeskApi";
import { StepUpSpinner } from "../../Components/Loader";
import useUser from "../../Core/hook/useUser";

/**
 * Staff Portal → Messages.
 *
 * UI-only redesign. Backend contract preserved verbatim:
 *   • `useGetMyMessagesQuery({filter,search,page,per_page})`
 *   • `useGetMyMessageThreadQuery({rootId})`
 *   • `useGetMyMessageRecipientsQuery({student_id})`
 *   • `useGetMyStudentsQuery()`
 *   • `useSendMyMessageMutation(...)` (root send + reply via
 *     parent_message_id)
 *   • `useMarkMyMessageReadMutation({id})`
 *
 * Route: /staff-portal/messages. Storage: portal_messages
 * (source='portal'), participant-filtered by Auth::user()->id;
 * new sends require `StaffTeachingScopeService::canAccessStudent`
 * and a parent user id legitimately linked to that student. Every
 * id is revalidated server-side — the client-only dropdown scoping
 * is UX, not security.
 */

type MsgRow = {
  id: number;
  thread_root_id: number;
  parent_message_id: number | null;
  direction: "inbound" | "outbound";
  student: { id: number; name: string; grade: string | null } | null;
  other_party: { user_id: number; name: string; role: string };
  subject: string;
  body: string;
  sent_at: string | null;
  is_read: boolean;
  class_occurrence_id: number | null;
};

type Recipient = {
  parent_id: number;
  user_id: number | null;
  has_login: boolean;
  is_primary: boolean;
  name: string;
  relationship: string;
};
type AdminRecipient = { id: number; name: string; role: string };
type MyStudent = { student_id: number; student_name: string; grade: string | null };
type SendMode = "parent" | "admin";

type TabKey = "inbox" | "unread" | "sent" | "all";

// ── Palette (Step Up blue / teal / green / rose; no amber) ────
const C = {
  blue:  { fg: "#1d4ed8", bg: alpha("#3b82f6", 0.10), border: alpha("#3b82f6", 0.28) },
  teal:  { fg: "#0f766e", bg: alpha("#14b8a6", 0.10), border: alpha("#14b8a6", 0.28) },
  green: { fg: "#15803d", bg: alpha("#22c55e", 0.10), border: alpha("#22c55e", 0.28) },
  indigo:{ fg: "#4338ca", bg: alpha("#6366f1", 0.10), border: alpha("#6366f1", 0.28) },
  rose:  { fg: "#b91c1c", bg: alpha("#ef4444", 0.08), border: alpha("#ef4444", 0.28) },
  navy:  { fg: "#0f172a", bg: "#f8fafc", border: "rgba(15,23,42,0.08)" },
};

const fmtTime = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const timeAgo = (iso: string | null): string => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const min = Math.max(1, Math.floor((now - then) / 60000));
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const initials = (name: string | undefined | null): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
};

// Quick-template rows come from `useGetMyActiveMessageTemplatesQuery`
// (staff-effective set: system defaults + user overrides + user
// customs, active only). There is deliberately NO hardcoded template
// array in this file — a change made on /staff-portal/message-templates
// invalidates the `messageTemplate` RTK tag and the composer picks up
// the new content automatically. See MessageTemplates.tsx for the
// management UI.
type QuickTemplate = { id: number; name: string; subject: string; body: string; default_key: string | null };

const applyTemplate = (tpl: QuickTemplate, studentName: string, parentName: string, staffName: string) => ({
  subject: tpl.subject
    .replaceAll("{studentName}", studentName || "your child")
    .replaceAll("{parentName}",  parentName  || "")
    .replaceAll("{staffName}",   staffName   || ""),
  body: tpl.body
    .replaceAll("{studentName}", studentName || "your child")
    .replaceAll("{parentName}",  parentName  || "there")
    .replaceAll("{staffName}",   staffName   || ""),
});

const Messages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { canCreate } = useUser();
  const canReply = canCreate("Message");

  const [tab, setTab] = useState<TabKey>("inbox");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [openThreadRootId, setOpenThreadRootId] = useState<number | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [sendErr, setSendErr] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useGetMyMessagesQuery({
    filter: tab, search: search || undefined, page, per_page: perPage,
  });
  const rows: MsgRow[] = useMemo(() => (data as any)?.data?.rows ?? [], [data]);
  const total: number = (data as any)?.data?.total ?? 0;
  const counts = (data as any)?.data?.counts ?? { unread: 0, sent_today: 0 };
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const hasActiveFilters = Boolean(search) || tab !== "inbox";

  const [markRead] = useMarkMyMessageReadMutation();

  const selectedRow = useMemo(
    () => rows.find((r) => r.thread_root_id === openThreadRootId) || null,
    [rows, openThreadRootId],
  );

  const openThread = (row: MsgRow) => {
    setOpenThreadRootId(row.thread_root_id);
    if (!row.is_read && row.direction === "inbound") {
      markRead({ id: row.id }).catch(() => { /* silent */ });
    }
  };

  const clearFilters = () => { setSearch(""); setTab("inbox"); setPage(1); };

  // ── Tabs + KPI config ────────────────────────────────────
  const tabs: Array<{ key: TabKey; label: string; count?: number }> = [
    { key: "inbox",  label: "Inbox",  count: undefined },
    { key: "unread", label: "Unread", count: Number(counts.unread ?? 0) },
    { key: "sent",   label: "Sent",   count: undefined },
    { key: "all",    label: "All",    count: undefined },
  ];

  const kpis: Array<{
    key: string; label: string; helper: string; value: number;
    tone: keyof typeof C; Icon: React.ElementType;
  }> = [
    {
      key: "unread", label: "Unread", helper: "Awaiting your reply",
      value: Number(counts.unread ?? 0),
      tone: "blue", Icon: MarkEmailReadOutlinedIcon,
    },
    {
      key: "sent", label: "Sent Today", helper: "Messages you sent",
      value: Number(counts.sent_today ?? 0),
      tone: "green", Icon: SendOutlinedIcon,
    },
    {
      key: "view", label: tab === "sent" ? "Sent" : tab === "unread" ? "Unread" : tab === "all" ? "All" : "Inbox",
      helper: `Total in ${tab === "sent" ? "Sent" : tab === "unread" ? "Unread" : tab === "all" ? "All" : "Inbox"}`,
      value: total,
      tone: "indigo", Icon: MoveToInboxOutlinedIcon,
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f4f7fb", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1720, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {/* ── HERO ─────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: "#fff", borderRadius: "16px", px: { xs: 2.5, md: 3 }, py: 2,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 2, mb: 2.5, flexWrap: "wrap",
            border: "1px solid rgba(15,23,42,0.06)",
            background: "linear-gradient(135deg, #ffffff 0%, #fafcff 55%, #f2f8fb 100%)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "12px",
                bgcolor: C.blue.bg, color: C.blue.fg,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ForumOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "1.4px", color: "#64748b", textTransform: "uppercase" }}>
                Staff Portal
              </Typography>
              <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", color: C.navy.fg, lineHeight: 1.2 }}>
                Messages
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Communicate with parents and guardians for students you teach.
              </Typography>
            </Box>
          </Stack>

          {canReply && (
            <Button
              variant="contained"
              startIcon={<SendOutlinedIcon />}
              onClick={() => setComposerOpen(true)}
              sx={{
                bgcolor: C.navy.fg, color: "#fff",
                fontWeight: 800, textTransform: "none", borderRadius: "12px",
                px: 2.25, py: 1, fontSize: 13.5,
                boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
                "&:hover": { bgcolor: "#1e293b", boxShadow: "0 6px 20px rgba(15,23,42,0.28)" },
              }}
            >
              New Message
            </Button>
          )}
        </Box>

        {/* ── KPI ROW ──────────────────────────────────────── */}
        <Box
          sx={{
            display: "grid", gap: 1.5, mb: 2.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          }}
        >
          {kpis.map((k) => {
            const tone = C[k.tone];
            return (
              <Box
                key={k.key}
                sx={{
                  bgcolor: "#fff", borderRadius: "14px", p: { xs: 1.5, md: 2 },
                  border: "1px solid rgba(15,23,42,0.06)",
                  display: "flex", alignItems: "center", gap: 1.5, minHeight: 82,
                }}
              >
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: "10px",
                    bgcolor: tone.bg, color: tone.fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <k.Icon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: tone.fg, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {isLoading ? "—" : k.value}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.5px", color: C.navy.fg, textTransform: "uppercase", mt: 0.5 }}>
                    {k.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    {k.helper}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── WORKSPACE: master/detail on desktop, list on mobile ── */}
        <Box
          sx={{
            display: "grid",
            gap: 1.75,
            gridTemplateColumns: { xs: "1fr", md: "minmax(340px, 40%) 1fr" },
          }}
        >
          {/* LEFT: message list */}
          <Box
            sx={{
              bgcolor: "#fff", borderRadius: "14px",
              border: "1px solid rgba(15,23,42,0.06)",
              display: "flex", flexDirection: "column",
              // Use viewport-anchored height so the list scrolls
              // independently of the detail panel on desktop.
              maxHeight: { md: "calc(100vh - 260px)" },
              minHeight: { xs: 420, md: 520 },
              overflow: "hidden",
            }}
          >
            {/* Tabs + search */}
            <Box sx={{ p: 1.5, borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
              <Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: "wrap", gap: 0.75 }}>
                {tabs.map((t) => {
                  const active = tab === t.key;
                  return (
                    <Chip
                      key={t.key}
                      label={
                        typeof t.count === "number" && t.count > 0 ? (
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <Box component="span">{t.label}</Box>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                minWidth: 18, height: 18, px: 0.75, borderRadius: 999,
                                fontSize: 10.5, fontWeight: 800,
                                bgcolor: active ? "rgba(255,255,255,0.24)" : C.blue.bg,
                                color: active ? "#fff" : C.blue.fg,
                              }}
                            >
                              {t.count}
                            </Box>
                          </Box>
                        ) : t.label
                      }
                      onClick={() => { setTab(t.key); setPage(1); }}
                      sx={{
                        height: 32, fontSize: 12.5, fontWeight: 700, borderRadius: "10px", cursor: "pointer",
                        bgcolor: active ? C.navy.fg : "#f1f5f9",
                        color: active ? "#fff" : "#334155",
                        border: active ? "none" : "1px solid rgba(15,23,42,0.06)",
                        "&:hover": { bgcolor: active ? "#1e293b" : "#e2e8f0" },
                        "& .MuiChip-label": { px: 1.25 },
                      }}
                    />
                  );
                })}
              </Stack>
              <TextField
                fullWidth size="small"
                placeholder="Search subject or message…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", height: 38, bgcolor: "#f8fafc",
                    "& fieldset": { borderColor: "rgba(15,23,42,0.08)" },
                    "&.Mui-focused fieldset": { borderColor: C.blue.fg, borderWidth: 1 },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setSearch(""); setPage(1); }} aria-label="Clear search">
                        <ClearOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Box>

            {/* Rows */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <Box sx={{ p: 1.5 }}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1, borderRadius: "10px", bgcolor: "rgba(15,23,42,0.04)" }} />
                  ))}
                </Box>
              ) : rows.length === 0 ? (
                <EmptyListState
                  hasFilters={hasActiveFilters}
                  onClear={clearFilters}
                  onNew={canReply ? () => setComposerOpen(true) : undefined}
                />
              ) : (
                <Box>
                  {rows.map((m) => {
                    const active = openThreadRootId === m.thread_root_id;
                    const unread = !m.is_read && m.direction === "inbound";
                    return (
                      <Box
                        key={m.id}
                        role="button" tabIndex={0}
                        onClick={() => openThread(m)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openThread(m); } }}
                        sx={{
                          px: 1.75, py: 1.25, cursor: "pointer",
                          borderBottom: "1px solid rgba(15,23,42,0.05)",
                          borderLeft: active ? `3px solid ${C.blue.fg}` : "3px solid transparent",
                          bgcolor: active ? C.blue.bg : unread ? alpha("#3b82f6", 0.04) : "#fff",
                          "&:hover": { bgcolor: active ? C.blue.bg : "#f8fafc" },
                          transition: "background 100ms ease",
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                              bgcolor: unread ? C.blue.bg : "#f1f5f9",
                              color: unread ? C.blue.fg : "#475569",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontWeight: 800, fontSize: 12.5,
                            }}
                          >
                            {initials(m.other_party?.name)}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Typography
                                sx={{
                                  fontSize: 13.5, color: C.navy.fg,
                                  fontWeight: unread ? 800 : 700,
                                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                  flex: 1, minWidth: 0,
                                }}
                              >
                                {m.direction === "inbound" ? m.other_party?.name : `To: ${m.other_party?.name}`}
                              </Typography>
                              <Typography sx={{ fontSize: 10.5, color: "text.disabled", fontWeight: 600, flexShrink: 0 }}>
                                {timeAgo(m.sent_at)}
                              </Typography>
                            </Stack>
                            {m.student && (
                              <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                Regarding {m.student.name}{m.student.grade ? ` · ${m.student.grade}` : ""}
                              </Typography>
                            )}
                            <Typography
                              sx={{
                                fontSize: 12.5, mt: 0.5, color: C.navy.fg,
                                fontWeight: unread ? 700 : 600,
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              }}
                            >
                              {m.subject}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11.5, color: "text.secondary", mt: 0.25,
                                display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden",
                              }}
                            >
                              {m.body}
                            </Typography>
                          </Box>
                          {unread && (
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: C.blue.fg, mt: 1, flexShrink: 0 }} />
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {pageCount > 1 && (
              <Box sx={{ p: 1, borderTop: "1px solid rgba(15,23,42,0.06)", display: "flex", justifyContent: "center" }}>
                <Pagination page={page} count={pageCount} onChange={(_, p) => setPage(p)} size="small" />
              </Box>
            )}
          </Box>

          {/* RIGHT: detail (desktop only). Mobile uses Drawer. */}
          {!isMobile && (
            <Box
              sx={{
                bgcolor: "#fff", borderRadius: "14px",
                border: "1px solid rgba(15,23,42,0.06)",
                display: "flex", flexDirection: "column",
                maxHeight: "calc(100vh - 260px)", minHeight: 520,
                overflow: "hidden",
              }}
            >
              {openThreadRootId ? (
                <ThreadContent
                  rootId={openThreadRootId}
                  selectedRow={selectedRow}
                  canReply={canReply}
                  onReplySent={() => setToast("Reply sent.")}
                  onError={setSendErr}
                  showHeaderClose={false}
                  onClose={() => setOpenThreadRootId(null)}
                />
              ) : (
                <EmptyDetailState
                  onNew={canReply ? () => setComposerOpen(true) : undefined}
                  isFetching={isFetching}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Mobile detail drawer */}
      {isMobile && (
        <Drawer
          anchor="right" open={!!openThreadRootId} onClose={() => setOpenThreadRootId(null)}
          PaperProps={{ sx: { width: "100%" } }}
        >
          {openThreadRootId && (
            <ThreadContent
              rootId={openThreadRootId}
              selectedRow={selectedRow}
              canReply={canReply}
              onReplySent={() => setToast("Reply sent.")}
              onError={setSendErr}
              showHeaderClose
              onClose={() => setOpenThreadRootId(null)}
            />
          )}
        </Drawer>
      )}

      {composerOpen && (
        <Composer
          onClose={() => setComposerOpen(false)}
          onSent={() => { setComposerOpen(false); setToast("Message sent."); }}
          onError={(m) => setSendErr(m)}
          fullScreen={isMobile}
        />
      )}

      <Snackbar
        open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast(null)} sx={{ fontWeight: 700 }}>
          {toast}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!sendErr} autoHideDuration={6000} onClose={() => setSendErr(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setSendErr(null)} sx={{ fontWeight: 700 }}>
          {sendErr}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═════════════ EMPTY STATES ═════════════
const EmptyListState: React.FC<{ hasFilters: boolean; onClear: () => void; onNew?: () => void }> = ({ hasFilters, onClear, onNew }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", px: 3, py: 5 }}>
    <Box
      sx={{
        width: 60, height: 60, borderRadius: "50%",
        bgcolor: hasFilters ? C.blue.bg : C.teal.bg,
        color: hasFilters ? C.blue.fg : C.teal.fg,
        display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5,
      }}
    >
      {hasFilters ? <SearchOutlinedIcon sx={{ fontSize: 28 }} /> : <MailOutlineOutlinedIcon sx={{ fontSize: 28 }} />}
    </Box>
    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: C.navy.fg }}>
      {hasFilters ? "No messages match your filters" : "No messages yet"}
    </Typography>
    <Typography sx={{ fontSize: 12, color: "text.secondary", maxWidth: 280, mt: 0.5 }}>
      {hasFilters
        ? "Try another tab or clear your search."
        : "Messages with parents and guardians will appear here."}
    </Typography>
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      {hasFilters && (
        <Button
          size="small" onClick={onClear} startIcon={<ClearOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: C.blue.fg, "&:hover": { bgcolor: alpha("#3b82f6", 0.08) } }}
        >
          Clear filters
        </Button>
      )}
      {!hasFilters && onNew && (
        <Button
          size="small" variant="contained" onClick={onNew} startIcon={<SendOutlinedIcon />}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 800, bgcolor: C.navy.fg, "&:hover": { bgcolor: "#1e293b" } }}
        >
          New Message
        </Button>
      )}
    </Stack>
  </Box>
);

const EmptyDetailState: React.FC<{ onNew?: () => void; isFetching: boolean }> = ({ onNew, isFetching }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", flex: 1, px: 3, py: 6 }}>
    <Box
      sx={{
        width: 76, height: 76, borderRadius: "50%",
        bgcolor: C.teal.bg, color: C.teal.fg,
        display: "flex", alignItems: "center", justifyContent: "center", mb: 2,
      }}
    >
      <ForumOutlinedIcon sx={{ fontSize: 36 }} />
    </Box>
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.navy.fg }}>
      Select a conversation
    </Typography>
    <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 340, mt: 0.75 }}>
      Choose a message from the inbox to view its history and reply.
    </Typography>
    {onNew && (
      <Button
        onClick={onNew} startIcon={<SendOutlinedIcon />}
        sx={{
          mt: 2.5, textTransform: "none", fontSize: 13, fontWeight: 800,
          bgcolor: C.navy.fg, color: "#fff", px: 2, py: 0.75, borderRadius: "10px",
          "&:hover": { bgcolor: "#1e293b" },
        }}
      >
        New Message
      </Button>
    )}
    {isFetching && <Box sx={{ mt: 2 }}><StepUpSpinner size="small" /></Box>}
  </Box>
);

// ═════════════ THREAD CONTENT (shared) ═════════════
const ThreadContent: React.FC<{
  rootId: number;
  selectedRow: MsgRow | null;
  canReply: boolean;
  onReplySent: () => void;
  onError: (msg: string) => void;
  showHeaderClose: boolean;
  onClose: () => void;
}> = ({ rootId, selectedRow, canReply, onReplySent, onError, showHeaderClose, onClose }) => {
  const { data, isLoading } = useGetMyMessageThreadQuery({ rootId });
  const [sendMsg, { isLoading: sending }] = useSendMyMessageMutation();
  const msgs: MsgRow[] = ((data as any)?.data ?? []) as MsgRow[];
  const root = msgs[0] || selectedRow;
  const [replyBody, setReplyBody] = useState("");
  useEffect(() => { setReplyBody(""); }, [rootId]);

  const handleReply = async () => {
    if (!root || !replyBody.trim()) return;
    try {
      await sendMsg({
        student_id: root.student?.id ?? 0,
        recipient_user_id: root.other_party.user_id,
        subject: root.subject.startsWith("Re:") ? root.subject : `Re: ${root.subject}`,
        body: replyBody.trim(),
        class_occurrence_id: root.class_occurrence_id ?? undefined,
        parent_message_id: root.thread_root_id,
      }).unwrap();
      onReplySent();
      setReplyBody("");
    } catch (err: any) {
      const status = err?.status ?? err?.originalStatus;
      const message = err?.data?.message;
      if (status === 403) onError(message || "You no longer have access to this recipient.");
      else if (status === 422) onError(message || "Please check the message and try again.");
      else onError(message || "We couldn't send this reply. Please try again.");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          {showHeaderClose && (
            <IconButton size="small" onClick={onClose} aria-label="Back to inbox">
              <ArrowBackOutlinedIcon />
            </IconButton>
          )}
          <Box
            sx={{
              width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
              bgcolor: C.blue.bg, color: C.blue.fg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: 13,
            }}
          >
            {initials(root?.other_party?.name)}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.navy.fg, lineHeight: 1.2 }}>
              {root?.other_party?.name || "Conversation"}
            </Typography>
            {root?.student && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary", mt: 0.5, flexWrap: "wrap" }}>
                <SchoolOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 12 }}>
                  Regarding <Box component="span" sx={{ fontWeight: 700, color: C.navy.fg }}>{root.student.name}</Box>
                  {root.student.grade ? ` · ${root.student.grade}` : ""}
                </Typography>
              </Stack>
            )}
            {root?.subject && (
              <Typography sx={{ fontSize: 12.5, color: C.navy.fg, fontWeight: 700, mt: 0.5 }}>
                {root.subject}
              </Typography>
            )}
          </Box>
          {showHeaderClose && (
            <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
          )}
        </Stack>
      </Box>

      {/* Timeline */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "#f8fafc", minHeight: 0 }}>
        {isLoading ? (
          <>
            <Skeleton height={80} sx={{ mb: 1.25, borderRadius: "10px" }} />
            <Skeleton height={80} sx={{ mb: 1.25, borderRadius: "10px" }} />
          </>
        ) : (
          msgs.map((m) => (
            <Box
              key={m.id}
              sx={{
                display: "flex",
                justifyContent: m.direction === "outbound" ? "flex-end" : "flex-start",
                mb: 1.5,
              }}
            >
              <Box sx={{ maxWidth: "78%" }}>
                <Box
                  sx={{
                    px: 1.75, py: 1.25, borderRadius: 2,
                    bgcolor: m.direction === "outbound" ? C.navy.fg : "#fff",
                    color: m.direction === "outbound" ? "#fff" : C.navy.fg,
                    border: m.direction === "outbound" ? "none" : "1px solid rgba(15,23,42,0.08)",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  }}
                >
                  <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                    {m.body}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5, textAlign: m.direction === "outbound" ? "right" : "left", fontWeight: 600 }}>
                  {m.direction === "outbound" ? "You" : m.other_party?.name} · {fmtTime(m.sent_at)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* Reply composer */}
      {canReply && root && (
        <Box sx={{ p: 2, borderTop: "1px solid rgba(15,23,42,0.06)", bgcolor: "#fff" }}>
          <TextField
            fullWidth multiline minRows={2} maxRows={6} size="small"
            placeholder="Write a reply…"
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px", bgcolor: "#f8fafc",
                "& fieldset": { borderColor: "rgba(15,23,42,0.08)" },
                "&.Mui-focused fieldset": { borderColor: C.blue.fg },
              },
            }}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="contained" size="small"
              startIcon={sending ? <StepUpSpinner size="small" /> : <ReplyOutlinedIcon />}
              disabled={sending || !replyBody.trim()}
              onClick={handleReply}
              sx={{
                textTransform: "none", fontWeight: 800, bgcolor: C.navy.fg,
                "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              {sending ? "Sending…" : "Send reply"}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// ═════════════ NEW MESSAGE COMPOSER ═════════════
const Composer: React.FC<{
  onClose: () => void;
  onSent: () => void;
  onError: (msg: string) => void;
  fullScreen: boolean;
}> = ({ onClose, onSent, onError, fullScreen }) => {
  const { userData } = useUser();
  const staffName: string = (userData as any)?.staff
    ?? (userData as any)?.username
    ?? "";
  const { data: studentsResp, isLoading: studentsLoading } = useGetMyStudentsQuery();
  const students: MyStudent[] = useMemo(
    () => ((studentsResp as any)?.data ?? []) as MyStudent[], [studentsResp],
  );
  // Template chips come from the saved-templates endpoint. Backend
  // enforces the effective-set rule and ownership; this hook just
  // renders whatever comes back. The list refetches automatically
  // when the templates page invalidates the `messageTemplate` tag,
  // so an edit made in another tab of the SPA is picked up next
  // render — no hard refresh required (§15, §16).
  const { data: tplResp } = useGetMyActiveMessageTemplatesQuery();
  const templates: QuickTemplate[] = useMemo(() => {
    const raw = ((tplResp as any)?.data ?? []) as Array<any>;
    return raw.map((t) => ({
      id: Number(t.id),
      name: String(t.name),
      subject: String(t.subject ?? ""),
      body: String(t.body ?? ""),
      default_key: t.default_key ?? null,
    }));
  }, [tplResp]);

  // Send mode — Parent / Guardian (default, existing flow) vs
  // Admin / Office (new peer-to-admin flow). Backend enforces the
  // recipient rules for each mode independently.
  const [mode, setMode] = useState<SendMode>("parent");
  const [studentId, setStudentId] = useState<number | "">("");
  const [recipientUserId, setRecipientUserId] = useState<number | "">("");
  const [adminUserId, setAdminUserId] = useState<number | "">("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const {
    data: recipResp,
    isFetching: recipLoading,
    isError: recipError,
    refetch: refetchRecipients,
  } = useGetMyMessageRecipientsQuery(
    { student_id: studentId || 0 },
    { skip: mode !== "parent" || !studentId },
  );
  const recipients: Recipient[] = useMemo(
    () => ((recipResp as any)?.data ?? []) as Recipient[], [recipResp],
  );

  const { data: adminResp, isFetching: adminLoading } = useGetMyAdminMessageRecipientsQuery(
    undefined,
    { skip: mode !== "admin" },
  );
  const adminRecipients: AdminRecipient[] = useMemo(
    () => ((adminResp as any)?.data ?? []) as AdminRecipient[], [adminResp],
  );

  // Auto-select: prefer the primary parent (relationship_tables.isPrimary),
  // then fall back to the first parent that has a portal login. Ignore
  // parents without a login — the current send-message contract requires
  // a real user id, so a login-less parent stays visible but disabled.
  useEffect(() => {
    if (mode !== "parent") return;
    if (!studentId) { setRecipientUserId(""); return; }
    const eligible = recipients.filter((r) => r.has_login && r.user_id != null);
    if (eligible.length === 0) { setRecipientUserId(""); return; }
    const primary = eligible.find((r) => r.is_primary) ?? eligible[0];
    setRecipientUserId(primary.user_id as number);
  }, [mode, studentId, recipients]);

  // Reset the OFF-mode selection so switching tabs cannot leak a
  // stale recipient into the payload.
  useEffect(() => {
    if (mode === "parent") setAdminUserId("");
    else { setStudentId(""); setRecipientUserId(""); }
  }, [mode]);

  const [sendMsg, { isLoading: sending }] = useSendMyMessageMutation();
  const [sendAdminMsg, { isLoading: sendingAdmin }] = useSendMyAdminMessageMutation();
  const anySending = sending || sendingAdmin;

  const selectedStudent = useMemo(
    () => students.find((s) => s.student_id === studentId) || null, [students, studentId],
  );
  const selectedRecipient = useMemo(
    () => recipients.find((r) => r.user_id === recipientUserId) || null, [recipients, recipientUserId],
  );
  const selectedAdmin = useMemo(
    () => adminRecipients.find((r) => r.id === adminUserId) || null, [adminRecipients, adminUserId],
  );

  const canSend = mode === "parent"
    ? Boolean(studentId && recipientUserId && subject.trim().length >= 3 && body.trim().length >= 3)
    : Boolean(adminUserId && subject.trim().length >= 3 && body.trim().length >= 3);

  const insertTemplate = (t: QuickTemplate) => {
    const filled = applyTemplate(
      t,
      selectedStudent?.student_name ?? "",
      selectedRecipient?.name?.split(/\s+/)[0] ?? "",
      staffName,
    );
    setSubject(filled.subject);
    setBody(filled.body);
  };

  const handleSend = async () => {
    if (!canSend) return;
    try {
      if (mode === "parent") {
        await sendMsg({
          student_id: Number(studentId),
          recipient_user_id: Number(recipientUserId),
          subject: subject.trim(),
          body: body.trim(),
        }).unwrap();
      } else {
        await sendAdminMsg({
          recipient_user_id: Number(adminUserId),
          subject: subject.trim(),
          body: body.trim(),
        }).unwrap();
      }
      onSent();
    } catch (err: any) {
      const status = err?.status ?? err?.originalStatus;
      const message = err?.data?.message;
      if (status === 403) onError(message || "You don't have permission to message this recipient.");
      else if (status === 422) onError(message || "Please check the required fields.");
      else onError(message || "We couldn't send this message. Please try again.");
    }
  };

  return (
    <Dialog
      open onClose={anySending ? undefined : onClose}
      fullWidth maxWidth="sm" fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : "16px" } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3, py: 2.25, position: "relative",
            background: `linear-gradient(135deg, ${C.navy.fg} 0%, #1e293b 100%)`,
            color: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "10px",
                bgcolor: "rgba(255,255,255,0.14)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <SendOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.2px", opacity: 0.75, textTransform: "uppercase" }}>
                Staff Portal · New Message
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.2, mt: 0.25 }}>
                New Message
              </Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.9, mt: 0.5 }}>
                {mode === "parent"
                  ? "Send a message to a parent or guardian for one of your students."
                  : "Send a message to an admin or office user."}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose} disabled={anySending} aria-label="Close"
            sx={{
              position: "absolute", top: 12, right: 12,
              color: "#fff", bgcolor: "rgba(255,255,255,0.14)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
              width: 30, height: 30,
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
        <SectionLabel>Send To</SectionLabel>
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v as SendMode)}
          fullWidth
          size="small"
          sx={{
            mb: 2, bgcolor: "#fff", borderRadius: "10px",
            "& .MuiToggleButton-root": {
              textTransform: "none", fontWeight: 700, fontSize: 13,
              border: "1px solid rgba(15,23,42,0.08)", color: "#64748b",
              py: 1,
              "&.Mui-selected": {
                bgcolor: C.blue.bg, color: C.blue.fg,
                borderColor: C.blue.border,
                "&:hover": { bgcolor: alpha("#3b82f6", 0.16) },
              },
            },
          }}
        >
          <ToggleButton value="parent">
            <FamilyRestroomOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
            Parent / Guardian
          </ToggleButton>
          <ToggleButton value="admin">
            <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
            Admin / Office
          </ToggleButton>
        </ToggleButtonGroup>

        <SectionLabel>Recipient</SectionLabel>
        {mode === "parent" ? (
          <Stack spacing={1.5}>
            <TextField
              select fullWidth size="small" label="Regarding Student *"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value === "" ? "" : Number(e.target.value))}
              disabled={studentsLoading}
              helperText={studentsLoading ? "Loading…" : "Only students you currently teach are available."}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              <MenuItem value="" disabled>Select a student</MenuItem>
              {students.map((s) => (
                <MenuItem key={s.student_id} value={s.student_id}>
                  {s.student_name}{s.grade ? ` — ${s.grade}` : ""}
                </MenuItem>
              ))}
            </TextField>

            {selectedStudent && (
              <Box
                sx={{
                  display: "flex", alignItems: "center", gap: 1.25,
                  p: 1.25, borderRadius: "10px", bgcolor: "#fff",
                  border: "1px solid rgba(15,23,42,0.06)",
                }}
              >
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: "10px",
                    bgcolor: C.teal.bg, color: C.teal.fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 12.5, flexShrink: 0,
                  }}
                >
                  {initials(selectedStudent.student_name)}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: C.navy.fg }}>
                    {selectedStudent.student_name}
                  </Typography>
                  {selectedStudent.grade && (
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                      {selectedStudent.grade}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {studentId && recipError ? (
              <Box sx={{
                p: 1.5, borderRadius: "10px", bgcolor: alpha("#ef4444", 0.06),
                border: `1px solid ${C.rose.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
              }}>
                <Typography sx={{ fontSize: 12.5, color: C.rose.fg }}>
                  We couldn't load this student's parent or guardian.
                </Typography>
                <Button
                  size="small"
                  onClick={() => refetchRecipients()}
                  sx={{ textTransform: "none", fontWeight: 700, color: C.rose.fg }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <TextField
                select fullWidth size="small" label="Parent / Guardian *"
                value={recipientUserId}
                onChange={(e) => setRecipientUserId(e.target.value === "" ? "" : Number(e.target.value))}
                disabled={!studentId || recipLoading}
                helperText={
                  !studentId
                    ? "Choose a student first."
                    : recipLoading
                      ? "Loading linked parents…"
                      : recipients.length === 0
                        ? "No parent or guardian is linked to this student."
                        : recipients.filter((r) => r.has_login).length === 0
                          ? "No parent portal login has been created for this student's guardian yet."
                          : recipients.length === 1
                            ? "Auto-selected — this student has one linked parent."
                            : "Primary guardian auto-selected. Switch if needed."
                }
                sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              >
                <MenuItem value="" disabled>Select a recipient</MenuItem>
                {recipients.map((r) => (
                  <MenuItem
                    key={r.parent_id}
                    value={r.user_id ?? ""}
                    disabled={!r.has_login || r.user_id == null}
                    sx={{ opacity: r.has_login ? 1 : 0.65 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>{r.name}</Box>
                        <Box component="span" sx={{ color: "text.secondary", ml: 0.5 }}>
                          — {r.relationship}
                          {r.is_primary ? " · Primary" : ""}
                        </Box>
                      </Box>
                      {!r.has_login && (
                        <Chip
                          label="No portal login"
                          size="small"
                          sx={{
                            height: 18, fontSize: 10, fontWeight: 700,
                            bgcolor: C.rose.bg, color: C.rose.fg, ml: "auto",
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}

            {selectedRecipient && (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "text.secondary", pl: 0.5 }}>
                <PersonOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 11.5 }}>
                  Messaging <Box component="span" sx={{ fontWeight: 700, color: C.navy.fg }}>{selectedRecipient.name}</Box> · {selectedRecipient.relationship}
                  {selectedRecipient.is_primary ? " · Primary" : ""}
                </Typography>
              </Stack>
            )}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <TextField
              select fullWidth size="small" label="Recipient *"
              value={adminUserId}
              onChange={(e) => setAdminUserId(e.target.value === "" ? "" : Number(e.target.value))}
              disabled={adminLoading}
              helperText={
                adminLoading
                  ? "Loading admin recipients…"
                  : adminRecipients.length === 0
                    ? "No admin or office recipients are available right now."
                    : "Only internal admin / office accounts are listed."
              }
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              <MenuItem value="" disabled>Select an admin recipient</MenuItem>
              {adminRecipients.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} — {r.role}
                </MenuItem>
              ))}
            </TextField>

            {selectedAdmin && (
              <Box
                sx={{
                  display: "flex", alignItems: "center", gap: 1.25,
                  p: 1.25, borderRadius: "10px", bgcolor: "#fff",
                  border: "1px solid rgba(15,23,42,0.06)",
                }}
              >
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: "10px",
                    bgcolor: C.indigo.bg, color: C.indigo.fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: C.navy.fg }}>
                    {selectedAdmin.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    {selectedAdmin.role}
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        )}

        <Box sx={{ mt: 3 }}>
          <SectionLabel>Message</SectionLabel>

          {/* Quick templates from saved Message Templates. Active-only
              feed; sorted by sort_order server-side. Clicking populates
              subject + body — user can then edit freely. */}
          {mode === "parent" && studentId && recipientUserId && templates.length > 0 && (
            <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.75 }}>
              {templates.map((t) => (
                <Tooltip key={t.id} title="Insert template" arrow>
                  <Chip
                    label={t.name}
                    onClick={() => insertTemplate(t)}
                    size="small"
                    sx={{
                      height: 26, fontSize: 11.5, fontWeight: 700, borderRadius: "8px",
                      bgcolor: C.blue.bg, color: C.blue.fg,
                      border: `1px solid ${C.blue.border}`,
                      "&:hover": { bgcolor: alpha("#3b82f6", 0.18) },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          )}

          <Stack spacing={1.5}>
            <TextField
              fullWidth size="small" label="Subject *"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              inputProps={{ maxLength: 150 }}
              helperText={`${subject.length} / 150`}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              fullWidth multiline minRows={8} maxRows={16} size="small" label="Message *"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              inputProps={{ maxLength: 5000 }}
              helperText={`${body.length} / 5000`}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff" }}>
        <Button onClick={onClose} disabled={anySending} sx={{ textTransform: "none", fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={anySending ? <StepUpSpinner size="small" /> : <SendOutlinedIcon />}
          disabled={!canSend || anySending}
          onClick={handleSend}
          sx={{
            textTransform: "none", fontWeight: 800, bgcolor: C.navy.fg,
            px: 2.25, borderRadius: "10px",
            "&:hover": { bgcolor: "#1e293b" },
          }}
        >
          {anySending ? "Sending…" : "Send Message"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "1.4px", color: "#64748b", textTransform: "uppercase", mb: 1 }}>
    {children}
  </Typography>
);

export default Messages;
